import React, { useEffect, useRef, useState } from "react";
import { useIntl } from "react-intl";
import { Button, Form, Message, Modal } from "semantic-ui-react";

import { getPurRecord, PurClientConfig, PurRecord } from "react-lib/apis/PUR/purApi";
import { getPurDocument, PurDocument } from "react-lib/apis/PUR/purDocumentsApi";
import { canVerifyPurDocuments, canVerifyPurDocumentType, performPurDocumentVerification, preparePurDocumentVerification, PurDocumentVerificationAccess, PurDocumentVerificationAttempt, PurDocumentVerificationError, reconcilePurDocumentVerification } from "react-lib/apis/PUR/purDocumentVerification";

interface Props {
  client: PurClientConfig;
  documentId: number;
  access?: PurDocumentVerificationAccess;
  onVerified: (document: PurDocument) => void;
}
interface Review { document: PurDocument; po: PurRecord | null }
type Result = "verified" | "alreadyVerified" | "unknown" | null;

const PurDocumentVerification: React.FC<Props> = ({ client, documentId, access, onVerified }) => {
  const intl = useIntl();
  const t = (id: string) => intl.formatMessage({ id });
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [review, setReview] = useState<Review | null>(null);
  const [acknowledged, setAcknowledged] = useState(false);
  const [result, setResult] = useState<Result>(null);
  const [error, setError] = useState<string | null>(null);
  const [missingPermissions, setMissingPermissions] = useState<string[]>([]);
  const request = useRef(0);
  const busy = useRef(false);
  const lastAttempt = useRef<PurDocumentVerificationAttempt | null>(null);
  // An ambiguous POST cannot be re-enabled by closing/reopening this dialog.
  const uncertain = useRef(false);
  const permitted = canVerifyPurDocuments(client, access);

  useEffect(() => {
    request.current += 1;
    busy.current = false;
    uncertain.current = false;
    setOpen(false); setReview(null); setAcknowledged(false); setResult(null);
    lastAttempt.current = null; setLoading(false); setSubmitting(false); setError(null); setMissingPermissions([]);
    return () => { request.current += 1; };
  }, [client, documentId, access]);

  const readReview = async () => {
    if (busy.current) return;
    const token = ++request.current;
    busy.current = true;
    setOpen(true); setLoading(true); setAcknowledged(false); setError(null); setMissingPermissions([]);
    setReview(null); setResult(uncertain.current ? "unknown" : null);
    try {
      let document = await getPurDocument(client, documentId);
      if (request.current !== token) return;
      if (document.id !== documentId) throw new Error("Unexpected document ID");
      if (document.verified === true) {
        if (uncertain.current) {
          if (!lastAttempt.current) throw new Error("No verification attempt available for reconciliation");
          const reconciled = await reconcilePurDocumentVerification(client, lastAttempt.current);
          if (request.current !== token) return;
          document = reconciled.document;
          uncertain.current = false;
          setResult("alreadyVerified"); onVerified(document);
          setReview({ document, po: reconciled.po });
          return;
        }
        setResult("alreadyVerified"); onVerified(document);
      }
      const po = document.poId === null ? null : await getPurRecord(client, "purchaseOrders", document.poId);
      if (request.current !== token) return;
      if (po && po.id !== document.poId) throw new Error("Unexpected PO ID");
      setReview({ document, po });
    } catch {
      if (request.current === token) setError("MEERP_VERIFY_READ_ERROR");
    } finally {
      if (request.current === token) { busy.current = false; setLoading(false); }
    }
  };

  const validReview = review !== null && review.document.verified === false
    && canVerifyPurDocumentType(review.document) && review.po !== null
    && review.document.vendorId !== null && review.document.vendorId === review.po.vendorId
    && review.document.projectId !== null && review.document.projectId === review.po.projectId
    && ["APPROVED", "PARTIALLY_RECEIVED", "TO_BILL", "TO_RECEIVE"].includes(review.po.status);

  const submit = async () => {
    if (busy.current || uncertain.current || !permitted || !validReview || !review || !acknowledged) return;
    const token = ++request.current;
    busy.current = true;
    setSubmitting(true); setError(null);
    let prepared: PurDocumentVerificationAttempt | null = null;
    try {
      // This is the write preflight: fresh document/PO/line snapshots, pilot
      // fixture/profile facts and just the action permission that progress needs.
      prepared = await preparePurDocumentVerification(client, documentId, access);
      if (request.current !== token) return;
      if (prepared.document.verified === true) {
        setResult("alreadyVerified"); onVerified(prepared.document); return;
      }
      if (JSON.stringify(prepared.document) !== JSON.stringify(review.document) || JSON.stringify(prepared.po) !== JSON.stringify(review.po)) {
        setReview(null); setAcknowledged(false); setError("MEERP_VERIFY_CHANGED"); return;
      }
      lastAttempt.current = prepared;
      // perform does one refreshed preflight, one POST and GET-only readback.
      uncertain.current = true;
      const outcome = await performPurDocumentVerification(client, prepared, access, () => request.current === token);
      if (request.current !== token) return;
      uncertain.current = false;
      setResult(outcome.verifiedByResponse ? "verified" : "alreadyVerified"); onVerified(outcome.document);
      setReview({ document: outcome.document, po: outcome.po });
    } catch (reason) {
      if (request.current !== token) return;
      if (!uncertain.current || (reason instanceof PurDocumentVerificationError && reason.outcome === "blocked")) {
        uncertain.current = false;
        setError(reason instanceof PurDocumentVerificationError ? reason.messageId : "MEERP_VERIFY_READ_ERROR");
        setMissingPermissions(reason instanceof PurDocumentVerificationError ? reason.permissionNames : []);
        return;
      }
      const status = (reason as { response?: { status?: number } } | null)?.response?.status;
      setResult("unknown");
      setError(status === 401 || status === 403 ? "MEERP_VERIFY_FORBIDDEN" : "MEERP_VERIFY_UNKNOWN");
      // 400 can mean already verified OR another validation error. Inspect state.
      // perform already attempted the only safe GET-only reconciliation after
      // a POST failure. Keep this outcome unresolved; never issue a retry.
    } finally {
      if (request.current === token) { busy.current = false; setSubmitting(false); }
    }
  };

  const close = () => {
    if (submitting) return;
    request.current += 1; busy.current = false; setLoading(false); setOpen(false);
  };
  const value = (v: string | number | null) => v === null || v === "" ? t("MEERP_UNKNOWN") : String(v);
  return <>
    {!permitted && <p className="card-meerp__dependency">{t("MEERP_VERIFY_ACCESS_PENDING")}</p>}
    <Button type="button" onClick={readReview}>{t("MEERP_VERIFY_REVIEW")}</Button>
    <Modal open={open} onClose={close} closeOnEscape={!submitting} closeOnDimmerClick={!submitting} size="small" role="dialog" aria-modal="true" aria-labelledby="meerp-verify-title">
      <Modal.Header id="meerp-verify-title">{t("MEERP_VERIFY_REVIEW")}</Modal.Header>
      <Modal.Content>
        <p>{t("MEERP_VERIFY_EFFECT")}</p>
        {!permitted && <Message warning content={t("MEERP_VERIFY_ACCESS_PENDING")} />}
        {loading && <p role="status">{t("MEERP_LOADING")}</p>}
        {error && <Message negative role="alert" content={t(error)} />}
        {missingPermissions.length > 0 && <ul>{missingPermissions.map((name) => <li key={name}><code>{name}</code></li>)}</ul>}
        {result === "unknown" && !error && <Message warning role="alert" content={t("MEERP_VERIFY_UNKNOWN")} />}
        {(result === "verified" || result === "alreadyVerified") && <Message positive role="status" content={t(result === "verified" ? "MEERP_VERIFY_SUCCESS" : "MEERP_VERIFY_ALREADY")} />}
        {review && <>
          <dl className="card-meerp__facts">{[
            ["MEERP_NUMBER", review.document.number], ["MEERP_VENDOR", review.document.vendorName],
            ["MEERP_TOTAL", review.document.grossAmount], ["MEERP_PRICE_VARIANCE", review.document.totalPriceVariance],
            ["MEERP_PO_ID", review.document.poId], ["MEERP_FIELD_PO_NUMBER", review.po?.number ?? null],
            ["MEERP_PERCENT_RECEIVED", review.po?.percentReceived ?? null], ["MEERP_PERCENT_BILLED", review.po?.percentBilled ?? null],
          ].map(([label, content]) => <div key={label}><dt>{t(String(label))}</dt><dd>{value(content)}</dd></div>)}</dl>
          <p>{t("MEERP_VERIFY_SERVER_PROGRESS")}</p>
          {!validReview && review.document.verified !== true && <Message warning content={t("MEERP_VERIFY_INELIGIBLE")} />}
          {validReview && !result && <Form><Form.Checkbox label={t("MEERP_VERIFY_ACKNOWLEDGE")} checked={acknowledged} disabled={!permitted || submitting} onChange={(_, data) => setAcknowledged(data.checked === true)} /></Form>}
        </>}
      </Modal.Content>
      <Modal.Actions>
        <Button type="button" disabled={submitting} onClick={close}>{t("MEERP_CLOSE_DETAIL")}</Button>
        <Button type="button" disabled={loading || submitting} onClick={readReview}>{t("MEERP_VERIFY_CHECK_STATUS")}</Button>
        <Button type="button" primary loading={submitting} disabled={!permitted || !validReview || !acknowledged || loading || submitting || result !== null} onClick={submit}>{t("MEERP_VERIFY_DOCUMENT")}</Button>
      </Modal.Actions>
    </Modal>
  </>;
};

export default PurDocumentVerification;
